import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState, Goal, GoalType, } from '../store';
import { v4 as uuidv4 } from 'uuid';
import { useT } from '../lib/i18n';
import { useDB } from '../lib/useDB';
import { useAuth } from '../hooks/useAuth';

export function GoalsPanel() {
  const { user } = useAuth();
  const { saveGoal, editGoal, removeGoal} = useDB(user);
  const goals     = useSelector((s: RootState) => s.goals.list);
  const sessions  = useSelector((s: RootState) => s.sessions.list);
  const settings  = useSelector((s: RootState) => s.settings);
  const { t }     = useT();

  const [creating, setCreating]       = useState(false);
  const [type, setType]               = useState<GoalType>('bankroll');
  const [description, setDescription] = useState('');
  const [targetValue, setTargetValue] = useState('');
  const [deadline, setDeadline]       = useState('');

  const totalProfit     = sessions.reduce((a, s) => a + s.netProfit, 0);
  const currentBankroll = settings.initialBankroll + totalProfit;
  const totalSessions   = sessions.length;
  const totalInvested   = sessions.reduce((a, s) => a + s.totalInvested, 0);
  const roi             = totalInvested > 0 ? (totalProfit / totalInvested) * 100 : 0;

  const getCurrentValue = (g: Goal): number => {
    switch (g.type) {
      case 'bankroll': return currentBankroll;
      case 'sessions': {
        if (!g.deadline) return totalSessions;
        const from = new Date(g.createdAt).toISOString().split('T')[0];
        return sessions.filter(s => s.date >= from && s.date <= g.deadline!).length;
      }
      case 'roi': return roi;
      default: return 0;
    }
  };

  const getProgress = (g: Goal): number => {
    const cur  = getCurrentValue(g);
    const base = g.type === 'bankroll' ? settings.initialBankroll : 0;
    const range = g.targetValue - base;
    if (range <= 0) return 100;
    return Math.min(100, Math.max(0, ((cur - base) / range) * 100));
  };

  const defaultDesc = (tp: GoalType, val: number): string => {
    switch (tp) {
      case 'bankroll': return t('goals.labelBankroll', { val: val || '?' });
      case 'sessions': return t('goals.labelSessions', { val: val || '?' });
      case 'roi':      return t('goals.labelRoi',      { val: val || '?' });
      default: return '';
    }
  };

  const handleCreate = () => {
    if (!targetValue) return;
    saveGoal({ id: uuidv4(), type, description: description || defaultDesc(type, parseFloat(targetValue)),
      targetValue: parseFloat(targetValue), deadline: deadline || null, createdAt: new Date().toISOString(), completed: false });
    setCreating(false); setDescription(''); setTargetValue(''); setDeadline('');
  };

  const goalLabel = (g: Goal): string => {
    const cur = getCurrentValue(g);
    switch (g.type) {
      case 'bankroll': return `$${cur.toFixed(2)} / $${g.targetValue.toFixed(2)}`;
      case 'sessions': return `${cur} / ${g.targetValue} ${t('goals.typeSessions').toLowerCase()}`;
      case 'roi':      return `${cur.toFixed(1)}% / ${g.targetValue}% ROI`;
      default: return '';
    }
  };
  const handleToggle = (goal: Goal) => {
    editGoal({
      ...goal,
      completed: !goal.completed,
    });
  };

  const targetLabel = type === 'bankroll' ? t('goals.targetBankroll') : type === 'sessions' ? t('goals.targetSessions') : t('goals.targetRoi');

  const active = goals.filter(g => !g.completed);
  const done   = goals.filter(g => g.completed);

  return (
    <div className="goals-panel">
      <div className="goals-header">
        <h2 className="form-title">{t('goals.title')}</h2>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>+ {t('goals.createGoal')}</button>
      </div>

      {creating && (
        <div className="panel goal-create">
          <div className="gc-row">
            <div className="form-field">
              <label className="field-label">{t('goals.type')}</label>
              <select value={type} onChange={e => setType(e.target.value as GoalType)}>
                <option value="bankroll">{t('goals.typeBankroll')}</option>
                <option value="sessions">{t('goals.typeSessions')}</option>
                <option value="roi">{t('goals.typeRoi')}</option>
              </select>
            </div>
            <div className="form-field">
              <label className="field-label">{targetLabel}</label>
              <input type="number" value={targetValue} onChange={e => setTargetValue(e.target.value)} />
            </div>
            <div className="form-field">
              <label className="field-label">{t('goals.deadline')}</label>
              <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} />
            </div>
          </div>
          <div className="form-field">
            <label className="field-label">{t('goals.description')}</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)}
              placeholder={defaultDesc(type, parseFloat(targetValue) || 0)} />
          </div>
          <div className="gc-actions">
            <button className="btn btn-primary" onClick={handleCreate}>{t('goals.createGoal')}</button>
            <button className="btn btn-secondary" onClick={() => setCreating(false)}>{t('goals.cancel')}</button>
          </div>
        </div>
      )}

      {active.length === 0 && !creating && (
        <div className="empty-state small"><p>{t('goals.empty')}</p></div>
      )}

      <div className="goals-list">
        {active.map(g => {
          const progress = getProgress(g);
          const achieved = getCurrentValue(g) >= g.targetValue;
          return (
            <div key={g.id} className={`goal-card ${achieved ? 'achieved' : ''}`}>
              <div className="goal-top">
                <span className="goal-type-badge">{g.type.toUpperCase()}</span>
                <span className="goal-desc">{g.description}</span>
                <div className="goal-actions">
                  {achieved && <button className="btn-done" onClick={() => handleToggle(g)}>✓ {t('goals.createGoal')}</button>}
                  <button className="btn-del-goal" onClick={() => removeGoal(g.id)}>✕</button>
                </div>
              </div>
              <div className="goal-label">{goalLabel(g)}</div>
              <div className="goal-bar-wrap"><div className="goal-bar" style={{ width: `${progress}%` }} /></div>
              <div className="goal-meta">
                <span>{progress.toFixed(0)}% {t('common.loading').toLowerCase()}</span>
                {g.deadline && <span>→ {g.deadline}</span>}
              </div>
            </div>
          );
        })}
      </div>

      {done.length > 0 && (
        <div className="goals-done-section">
          <div className="panel-title">{t('goals.completed')}</div>
          {done.map(g => (
            <div key={g.id} className="goal-card done">
              <span className="goal-desc">✓ {g.description}</span>
              <button className="btn-del-goal" onClick={() => removeGoal(g.id)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
